import { InfoOutlined } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { type Company, type MarketDataSearch, type User } from "@prisma/client";
import { type NextPage } from "next";
import { type GetServerSideProps } from "nextjs-routes";
import { useState } from "react";
import { value } from "~/components/helpers";
import {
  type Input as MarketDataInput,
  mapMarketDataInput,
  MarketDataBrowser,
} from "~/components/market-data/MarketDataBrowser";
import { Button } from "~/components/ui/core/Button";
import { LegacyModal } from "~/components/ui/core/LegacyModal";
import { NextLinkComposed } from "~/components/ui/core/Link";
import { LocationList } from "~/components/ui/LocationList";
import { useListJobsQuery } from "~/hooks/useQueries";
import { logWarn } from "~/lib/logger";
import { parseString } from "~/lib/queryParams";
import { redirect } from "~/lib/redirect";
import { ssr } from "~/lib/ssr";
import { arrayHasValues } from "~/lib/utils";
import { type GetMarketDataStatsResponse, GetMarketDataStatsSchema } from "~/pages/api/get-market-data-stats";
import { getDefaultCurrency } from "~/services/currency";
import { formatJobsAndCustomJobsIds } from "~/services/custom-jobs/fetchJobsAndCustomJobs";
import { type EmployeeLocationWithCountry, getDefaultLocations } from "~/services/employee/employeeLocation";
import { getMarketDataStats } from "~/services/market-data/getMarketDataStats";
import { buildMarketDataQuery } from "~/services/market-data/market-data-filters/buildMarketDataQuery";
import { fetchMarketDataSearch } from "~/services/marketDataSearch";

const handler: GetServerSideProps<Props, "/share/[hash]"> = async (ctx) => {
  const search = await fetchMarketDataSearch(ctx.req, parseString(ctx.query, "hash"));

  const validatedInputJson = await value(async () => {
    try {
      return GetMarketDataStatsSchema.omit(["market", "compare", "mergeAdvancedLevels"]).validateSync(search.input);
    } catch (error) {
      return null;
    }
  });

  if (!validatedInputJson) {
    logWarn(ctx.req, "[market-data] Invalid input for share page", { hash: search.hash, input: search.input });

    return redirect({ pathname: "/link-expired" });
  }

  const currency = validatedInputJson.currency
    ? await ctx.req.prisma.currency.findUniqueOrThrow({ where: { code: validatedInputJson.currency } })
    : await getDefaultCurrency(ctx.req);

  const defaultLocations = await getDefaultLocations(ctx.req);

  const initialInput: MarketDataInput = {
    measure: validatedInputJson.measure,
    levels: validatedInputJson.levels,
    benchmarkLevelIds: validatedInputJson.benchmarkLevelIds,
    useBenchmarkLevelsInShare: arrayHasValues(validatedInputJson.benchmarkLevelIds), // for shares without a user
    mergeAdvancedLevels: !!search.input.mergeAdvancedLevels,
    showCompanyEmployees: validatedInputJson.showCompanyEmployees,
    currency,
    percentiles: validatedInputJson.percentiles,
    market: {
      fundingRounds: search.marketFilters?.fundingRounds ?? [],
      headcount: search.marketFilters?.headcount,
      industries: search.marketFilters?.industries ?? [],
      industry: search.marketFilters?.industry ?? null,
      jobIds: formatJobsAndCustomJobsIds({ jobs: search.jobs, customJobs: search.customJobs }),
      labels: search.marketFilters?.labels ?? [],
      locations: search.marketFilters?.locations.length ? search.marketFilters?.locations : defaultLocations,
      size: {
        min: search.marketFilters?.minEmployeesCount ?? null,
        max: search.marketFilters?.maxEmployeesCount ?? null,
      },
    },
    compare: {
      enabled: !!search.comparedMarketFilters,
      fundingRounds: search.comparedMarketFilters?.fundingRounds ?? [],
      headcount: search.comparedMarketFilters?.headcount ?? null,
      industries: search.comparedMarketFilters?.industries ?? [],
      industry: search.comparedMarketFilters?.industry ?? null,
      jobIds: formatJobsAndCustomJobsIds({ jobs: search.comparedJobs, customJobs: search.comparedCustomJobs }),
      labels: search.comparedMarketFilters?.labels ?? [],
      locations: search.comparedMarketFilters?.locations.length
        ? search.comparedMarketFilters?.locations
        : defaultLocations,
      size: {
        min: search.comparedMarketFilters?.minEmployeesCount ?? null,
        max: search.comparedMarketFilters?.maxEmployeesCount ?? null,
      },
    },
  };

  // If the user is logged in, we can re-encode the search within the query string and redirect to it.
  if (ctx.req.user) {
    const query = buildMarketDataQuery(
      { input: initialInput, defaultLocations, user: ctx.req.user },
      { canAccessFiguresAiV2: ctx.req.featureFlags.CAN_ACCESS_FIGURES_AI_V2 }
    );

    return redirect({
      pathname: "/market-data",
      query: {
        ...query,
        origin: search.source,
      },
    });
  }

  // Otherwise we can perform the search and render a read-only market data browser.
  const initialStats = await value(async () => {
    try {
      return await getMarketDataStats(
        ctx.req,
        {
          ...mapMarketDataInput(initialInput),
          useTestData: validatedInputJson.useTestData,
        },
        null
      );
    } catch (error) {
      return null;
    }
  });

  return {
    props: {
      initialInput,
      initialStats,
      defaultLocations,
      search,
    },
  };
};

export const getServerSideProps = ssr(handler, {
  authentication: { optional: true },
});

type Props = {
  initialInput: MarketDataInput;
  initialStats: GetMarketDataStatsResponse | null;
  defaultLocations: EmployeeLocationWithCountry[];
  search: MarketDataSearch & {
    user: User & {
      company: Company | null;
    };
  };
};

const Page: NextPage<Props> = ({ initialInput, initialStats, defaultLocations, search }) => {
  const [showInstructionsModal, setShowInstructionsModal] = useState(true);

  const { data: jobsData } = useListJobsQuery();
  const jobTitles = jobsData?.jobs
    .filter((job) => initialInput.market.jobIds.includes(`${job.id}`))
    .map((job) => job.name)
    .join(", ");

  return (
    <>
      <LegacyModal
        isOpen={showInstructionsModal}
        title="Welcome 👋"
        onClose={() => {
          setShowInstructionsModal(false);
        }}
      >
        <div className="ml-1 mt-3 flex flex-col">
          <span className="space-x-1 text-xl">
            <span className="font-semibold">
              {search.user.firstName} {search.user.lastName}
            </span>
            {search.user.company && (
              <span>
                from <span className="font-semibold">{search.user.company.name}</span>
              </span>
            )}
            <span>shared some Figures compensation data with you.</span>
          </span>

          <div className="mt-4 flex flex-col space-y-4 border-t pt-4">
            <Typography variant="h2">What am I looking at?</Typography>

            <div className="flex flex-col space-y-2 text-lg">
              <span>
                · Market compensation data for the role of <span className="font-semibold">{jobTitles}</span>
              </span>

              {initialInput.market.locations.length > 0 && (
                <span className="flex">
                  <span>· The data is filtered for the following locations:</span>
                  <span className="ml-1 font-semibold">
                    <LocationList locations={initialInput.market.locations} />
                  </span>
                </span>
              )}

              <span>
                · The amounts you're seeing represent <span className="font-semibold">total cash</span> (base salary +
                variable bonuses). You can change it to base salary by using the top left button.
              </span>
            </div>
          </div>

          <div className="ml-auto mt-10 flex space-x-2 self-end">
            <Button component={NextLinkComposed} variant="outlined" startIcon={<InfoOutlined />} to="/home">
              Learn more about Figures
            </Button>
            <Button variant="contained" onClick={() => setShowInstructionsModal(false)}>
              Continue to data &rarr;
            </Button>
          </div>
        </div>
      </LegacyModal>

      <MarketDataBrowser
        readOnly
        defaultLocations={defaultLocations}
        initialInput={initialInput}
        initialStats={initialStats}
        onReadOnlyInstructionsClick={() => {
          setShowInstructionsModal(true);
        }}
        filtersPresets={[]}
      />
    </>
  );
};

export default Page;
